import { ob, savedPlots, newPlotRow } from '@/services/onboardingStore'
import { saveIdentity, saveLocation, saveBankDetails, uploadLogo, saveBillingConfig } from '@/services/parkConfig'
import { saveOnboardingPlots } from '@/services/onboarding'
import { createContract, addContact } from '@/services/contracts'
import {
  isValidRfc, isValidEmail, isValidClabe, clabeDigits, buildEscalations, CUSTOM_ZONE,
} from './onboardingFormat'

// Cada función guarda lo de su paso. Devuelve true si se puede avanzar; si no, deja el motivo en ob.error.
const fail = (message) => {
  ob.error = message
  return false
}

export const plotZone = (row) => (row.zone === CUSTOM_ZONE ? row.customZone.trim() : row.zone)

export async function submitPark() {
  const p = ob.park
  if (p.name.trim().length < 2) return fail('Escribe el nombre de tu parque.')
  if (p.rfc.trim() && !isValidRfc(p.rfc)) return fail('El RFC no tiene un formato válido. Revisa las letras y los 6 dígitos de fecha.')
  if (p.contactEmail.trim() && !isValidEmail(p.contactEmail)) return fail('El correo de contacto no es válido.')
  if (p.bankClabe.trim() && !isValidClabe(p.bankClabe)) return fail('La CLABE debe tener exactamente 18 dígitos.')

  await saveIdentity(ob.parkId, {
    name: p.name.trim(),
    rfc: p.rfc.trim().toUpperCase() || null,
    contactEmail: p.contactEmail.trim() || null,
  })
  if (p.address.trim() || p.city.trim() || p.state.trim()) {
    await saveLocation(ob.parkId, { address: p.address.trim(), city: p.city.trim(), state: p.state.trim() })
  }
  const hasBank = !!(p.bankName.trim() || p.bankBeneficiary.trim() || p.bankClabe.trim())
  if (hasBank) {
    await saveBankDetails(ob.parkId, {
      showBankDetails: true,
      bankName: p.bankName.trim() || null,
      bankBeneficiary: p.bankBeneficiary.trim() || null,
      bankClabe: clabeDigits(p.bankClabe) || null,
    })
  }
  if (p.logoFile) {
    const res = await uploadLogo(ob.parkId, p.logoFile)
    if (res?.logoUrl) p.logoUrl = res.logoUrl
    p.logoFile = null
  }
  return true
}

export async function submitPlots() {
  const pending = ob.plots.filter((r) => !r.saved && (r.name.trim() || String(r.areaM2).trim()))
  if (!pending.length) {
    if (savedPlots().length) return true
    return fail('Agrega al menos una nave, o usa "Omitir este paso".')
  }
  const seen = new Set(savedPlots().map((r) => r.name.trim().toLowerCase()))
  for (const row of pending) {
    const name = row.name.trim()
    if (!name) return fail('Cada nave necesita un código, por ejemplo A-01.')
    if (seen.has(name.toLowerCase())) return fail(`El código "${name}" está repetido.`)
    seen.add(name.toLowerCase())
    const area = String(row.areaM2).trim()
    if (area && !(Number(area) > 0)) return fail(`Los metros cuadrados de "${name}" deben ser un número mayor a 0.`)
    if (row.zone === CUSTOM_ZONE && !row.customZone.trim()) return fail(`Escribe el nombre de la zona de "${name}".`)
  }

  const { plots } = await saveOnboardingPlots(
    ob.parkId,
    pending.map((r) => ({ name: r.name.trim(), areaM2: String(r.areaM2).trim() || null, zone: plotZone(r) || null })),
  )
  pending.forEach((row, i) => {
    row.saved = true
    row.id = plots[i]?.id || null
  })
  // Las filas vacías que sobraron se quitan, y se deja una nueva en blanco para seguir agregando.
  ob.plots = ob.plots.filter((r) => r.saved || r.name.trim())
  ob.plots.push(newPlotRow())
  return true
}

export async function submitTenant() {
  if (ob.contractCreated || ob.contractsImported > 0) return true
  const t = ob.tenant
  if (!t.name.trim()) return fail('Escribe el nombre de la empresa inquilina.')
  if (t.rfc.trim() && !isValidRfc(t.rfc)) return fail('El RFC de la empresa no tiene un formato válido.')
  if (t.contactEmail.trim() && !isValidEmail(t.contactEmail)) return fail('El correo del contacto de pagos no es válido.')
  if (!t.plotId) return fail('Elige la nave que ocupa este inquilino.')
  return true
}

export async function submitContract() {
  if (ob.contractCreated || ob.contractsImported > 0) return true
  const t = ob.tenant
  const c = ob.contract
  if (!t.name.trim() || !t.plotId) return fail('Primero agrega a tu inquilino en el paso anterior, o usa "Omitir este paso".')
  const rent = Number(c.rent)
  if (!(rent > 0)) return fail('Escribe la renta mensual.')
  if (!c.startDate || !c.endDate) return fail('Indica la fecha de inicio y la de vencimiento.')
  if (c.endDate <= c.startDate) return fail('El vencimiento debe ser posterior a la fecha de inicio.')
  const percent = Number(c.escalationPercent)
  if (c.escalate && !(percent > 0 && percent <= 100)) return fail('El porcentaje de escalación debe estar entre 0 y 100.')

  const opening = String(c.openingBalance || '').trim()
  if (opening && !(Number(opening) > 0)) return fail('El saldo pendiente anterior debe ser un número mayor a 0.')

  const plot = ob.plots.find((r) => r.id === t.plotId)
  const created = await createContract({
    plotId: t.plotId,
    startDate: c.startDate,
    endDate: c.endDate,
    monthlyRent: rent,
    contractType: 'FIXED',
    escalations: c.escalate ? buildEscalations(c.startDate, c.endDate, rent, percent) : [],
    newTenant: { name: t.name.trim(), rfc: t.rfc.trim().toUpperCase() || null, contactEmail: t.contactEmail.trim() || null },
    ...(opening ? { openingBalance: { amount: Number(opening) } } : {}),
  })
  ob.contractCreated = { id: created.id, tenantName: t.name.trim(), plotName: plot?.name || '', rent }
  // El contacto de pagos es el que recibe recordatorios y estados de cuenta.
  if (t.contactName.trim()) {
    try {
      await addContact(created.id, { name: t.contactName.trim(), role: 'Contacto de pagos', roleType: 'PAYMENTS', email: t.contactEmail.trim() || null })
    } catch {
      // El contrato ya existe; el contacto se puede agregar después desde Contratos.
    }
  }
  return true
}

export async function submitBilling() {
  const b = ob.billing
  const rules = [
    ['rentDueDay', 1, 28, 'El día de vencimiento de la renta debe estar entre 1 y 28.'],
    ['serviceDueDays', 0, 120, 'Los días de plazo de servicios deben estar entre 0 y 120.'],
    ['maintenanceDueDays', 0, 120, 'Los días de plazo de mantenimiento deben estar entre 0 y 120.'],
    ['manualChargeDueDaysDefault', 0, 120, 'Los días sugeridos para cargos manuales deben estar entre 0 y 120.'],
  ]
  for (const [key, min, max, message] of rules) {
    const n = Number(b[key])
    if (!Number.isInteger(n) || n < min || n > max) return fail(message)
  }
  let billingStartDate = null
  if (b.startMode === 'HISTORY') {
    const now = new Date()
    const currentMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`
    if (!/^\d{4}-\d{2}$/.test(b.historyFrom || '')) return fail('Elige desde qué mes quieres incluir el historial.')
    if (b.historyFrom > currentMonth) return fail('El mes de inicio del historial no puede ser futuro.')
    if (Number(b.historyFrom.slice(0, 4)) < 1990) return fail('El mes de inicio del historial no es válido.')
    billingStartDate = `${b.historyFrom}-01T00:00:00.000Z`
  }
  await saveBillingConfig(ob.parkId, {
    billingStartDate,
    historyAssumedPaid: b.startMode === 'HISTORY' ? !!b.historyAssumedPaid : true,
    rentDueDay: Number(b.rentDueDay),
    serviceDueDays: Number(b.serviceDueDays),
    maintenanceDueDays: Number(b.maintenanceDueDays),
    manualChargeDueDaysDefault: Number(b.manualChargeDueDaysDefault),
  })
  return true
}

export const SUBMITTERS = [submitPark, submitPlots, submitTenant, submitContract, submitBilling]